import { supabase } from './supabase'

const MAX_FILE_SIZE = 2 * 1024 * 1024 // 2MB in bytes
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/gif']

export interface ValidationResult {
  valid: boolean
  error?: string
}

export const avatarService = {
  /**
   * Validate avatar file client-side
   */
  validateAvatarFile(file: File): ValidationResult {
    if (file.size > MAX_FILE_SIZE) {
      return {
        valid: false,
        error: 'الصورة كبيرة جداً. الحد الأقصى 2 ميجابايت'
      }
    }

    if (!ALLOWED_TYPES.includes(file.type)) {
      return {
        valid: false,
        error: 'نوع الملف غير مدعوم. استخدم JPG، PNG، أو WebP'
      }
    }

    return { valid: true }
  },

  /**
   * Upload avatar image to Supabase Storage
   * Returns the public URL of the uploaded image
   */
  async uploadAvatar(userId: string, file: File): Promise<string> {
    // Get file extension
    const fileExt = file.name.split('.').pop()
    const fileName = `avatar.${fileExt}`
    const filePath = `${userId}/${fileName}`

    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: true // Overwrite if exists
      })

    if (uploadError) {
      console.error('Avatar upload error:', uploadError)
      throw new Error('فشل رفع الصورة. حاول مرة أخرى')
    }

    // Get public URL
    const { data } = supabase.storage
      .from('avatars')
      .getPublicUrl(filePath)

    // Add cache-busting query param to force reload
    const cacheBuster = `?t=${Date.now()}`
    return data.publicUrl + cacheBuster
  },

  /**
   * Delete avatar from storage
   */
  async deleteAvatar(userId: string): Promise<void> {
    // List all files in user's folder
    const { data: files, error: listError } = await supabase.storage
      .from('avatars')
      .list(userId)

    if (listError) {
      console.error('Avatar list error:', listError)
      throw new Error('فشل حذف الصورة')
    }

    if (!files || files.length === 0) {
      // No files to delete
      return
    }

    // Delete all files in the folder
    const filePaths = files.map(file => `${userId}/${file.name}`)
    const { error: deleteError } = await supabase.storage
      .from('avatars')
      .remove(filePaths)

    if (deleteError) {
      console.error('Avatar delete error:', deleteError)
      throw new Error('فشل حذف الصورة')
    }
  },

  /**
   * Update profile avatar_url in database
   */
  async updateProfileAvatar(userId: string, avatarUrl: string | null): Promise<void> {
    const { error } = await supabase
      .from('profiles')
      .update({ avatar_url: avatarUrl })
      .eq('id', userId)

    if (error) {
      console.error('Profile update error:', error)
      throw new Error('فشل تحديث الملف الشخصي')
    }
  }
}
